import { Button } from "@material-ui/core";
import axios from "axios";
import useAdminRights from "backend/hooks/misc/useAdminRights";
import usePeriodLevels from "backend/hooks/model/usePeriodLevels";
import { IPeriodLevel } from "backend/model/model";
import clsx from "clsx";
import GenericToolbar from "components/toolbars/GenericToolbar";
import { Form, Formik, FormikProps } from "formik";
import { t } from "i18next";
import _ from "lodash";
import useMeaQuery from "qpr-base/apihooks/useMeaQuery";
import * as conf from "qpr-base/configuration";
import { getKpisAndProjectsManagementHierarchyMode } from "qpr-base/configuration";
import { useScrollContext } from "qpr-base/context/ScrollContextProvider";
import Anchor from "qpr-base/ui/Anchor";
import Loader from "qpr-base/ui/components/Loader";
import PageContentMenu from "qpr-base/uicomponents/view/PageContentMenu";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ComponentThemes from "../../kpisandprojectsmanagement/themes";
import BasicInfoForm from "../BasicInfoForm";
import ElementLocationForm from "../ElementLocationForm";
import useProjectKPIQueries from "../ProjectAndKPIQueries";
import StatusHandler from "../StatusHandler";
import SubInfo from "../SubItems/SubInfo";
import SubmitProjectForm, {
  ApproveProjectForm,
  RejectProjectForm,
} from "../SubmitProjectForm";
import ValuesForm from "../ValuesForm";
import { mapWorkflowToProjectValues } from "../dataMapping/dataMapperProject";
import {
  PROJECT_EMPTY_INITIAL_VALUES as EMPTY_INITIAL_VALUES,
  Project_ValidationSchema as ValidationSchema,
} from "../initialValuesAndValidators";
import ElementOwnershipForm from "./../KPIsForm/KPIOwnershipForm";
import MeasurementDetailsForm from "./../KPIsForm/MeasurementDetailsForm";
import { Value } from "./../KPIsForm/index.dto";
import useFormStyles, { useButtonStyles } from "./../KPIsForm/themes";
import ScheduleAndBudgetForm from "./ScheduleAndBudgetForm";
import { ProjectValues } from "./index.dto";

const ProjectsForm = () => {
  const classes = useFormStyles();
  const buttonClasses = useButtonStyles();
  const [GEOptions, setGEOptions] = useState<any[]>([]);
  const [selectedGE, setSelectedGE] = useState<string>("");
  const [themeOptions, setThemeOptions] = useState<any[]>([]);
  const [prioritiesOptions, setPrioritiesOptions] = useState<any[]>([]);
  const [INITIAL_VALUES, setINITIAL_VALUES] =
    useState<ProjectValues>(EMPTY_INITIAL_VALUES);
  const [allowedActions, setAllowedActions] = useState<string[]>([
    "submit-enabled",
    "allowedit",
    "approve-enabled",
    "reject-enabled",
  ]);
  const isAdmin = useAdminRights();
  const [status, setStatus] = useState("idle");
  const [loading, setLoading] = useState(false);
  const [editWfId, setEditWfId] = useState<string>("");
  const [visiblePeriods, setVisiblePeriods] = useState<string[]>([]);
  const [editWfType, setEditWfType] = useState<string>("");
  const [approveCount, setApproveCount] = useState<number>(0);
  const originalLocation = useLocation().pathname.split("/");
  const params = new URLSearchParams(window.location.search);
  const [invalidAccess, setInvalidAccess] = useState<boolean>(false);
  const selectedMeaElement = useMeaQuery<
    { id: string; name: string; valuesettingid: string; typename: string }[]
  >(`[${params.get("id")}]`, "id, name, valuesettingid, typename", undefined, {
    enabled: params.get("id") !== "",
  });

  const setPeriods = (
    periodlevelId: string,
    startDate: string,
    endDate: string
  ) => {
    if (!periodlevelId || !startDate || !endDate) {
      setVisiblePeriods([]);
      return;
    }
    const level = periodlevels?.find((level) => level.id === periodlevelId);
    if (!level) {
      setVisiblePeriods([]);
      return;
    }
    let periods = level.periods;
    if (startDate && endDate) {
      // filter with given date interval
      periods = level.periods.filter((period) => {
        return (
          new Date(period.startdate.substring(0, 10)) >= new Date(startDate) &&
          new Date(period.enddate.substring(0, 10)) <= new Date(endDate)
        );
      });
    }
    setVisiblePeriods(periods.map((p) => p.name));
  };

  const milestonesRef = useRef();
  const scrollTo = useScrollContext((state) => state.scrollTo);

  const validateSubForms = () => {
    const milestones = milestonesRef?.current as any;
    if (
      milestones?.dirty ||
      JSON.stringify(milestones?.errors || {}) !== JSON.stringify({})
    ) {
      alert(t("Please complete the current milestone form before submitting."));

      scrollTo("milestones");
      return false;
    }
    return true;
  };

  const history = useHistory();

  const { data: periodlevels, isLoading: periodlevelsLoading } =
    usePeriodLevels();

  const [editLoading, setEditLoading] = useState<boolean>(false);

  const [meaId, setMeaId] = useState<string>("");
  const {
    data,
    isLoading,
    valueSettings,
    valueSettingsLoading,
    roleData,
    roleLoading,
    units,
    unitsLoading,
  } = useProjectKPIQueries();

  const preferredScorecard = "SC1467324005"; // preferred scorecard (symbol) for new elements

  const hierarchyMode = getKpisAndProjectsManagementHierarchyMode();

  const editType = params.get("type") || "";

  const themeOptionsBySymbol = useMemo(() => {
    // return themeOptions.filter((opt: any) => opt.scorecardid === selectedGE);
    return _.groupBy(themeOptions, "symbol");
  }, [themeOptions]);

  const priorityOptionsBySymbol = useMemo(() => {
    /*
    return prioritiesOptions.filter(
      (opt: any) => opt.scorecardid === selectedGE
    );
    */
    return _.groupBy(prioritiesOptions, "symbol");
  }, [prioritiesOptions]);

  const filterByTheme = (theme: any) => {
    if (!theme || !theme.symbol || !themeOptionsBySymbol[theme.symbol]) return;
    const options = themeOptionsBySymbol[theme.symbol] || [];

    // if (selectedGE)
    //  options = options.filter((opt) => opt.scorecardid === selectedGE);
    const allPriorityAreas = _.flatten(
      options.map((option: any) => option.childobjects.split(","))
    );
    setPrioritiesOptions([
      ...new Set(
        data?.data?.filter((d: any) => {
          if (d.typename !== "Strategic Objective") {
            return false;
          }
          return allPriorityAreas.includes(d.id);
        }) || []
      ),
    ]);
    const allScorecardIds = options.map((option: any) => option.scorecardid);
    const geData: any = [
      ...new Set(
        data?.data?.filter(
          (d: any) => !d.parentid && allScorecardIds.includes(d.scorecardid)
        ) || []
      ),
    ];
    setSelectedGE((prev) => {
      return geData.find((ge: any) => ge.id === prev)?.id || "";
    });
  };

  useEffect(() => {
    if (!selectedMeaElement.isLoading) {
      if (params.get("id") !== selectedMeaElement.data?.data[0]?.id) {
        params.set(
          "id",
          selectedMeaElement.data?.data[0]?.id || params.get("id") || ""
        );

        history.replace({
          pathname: window.location.pathname,
          search: params.toString(),
        });
      }
    }
  }, [selectedMeaElement.isLoading, selectedMeaElement.data]);

  useEffect(() => {
    if (
      !data ||
      isLoading ||
      unitsLoading ||
      valueSettingsLoading ||
      roleLoading ||
      selectedMeaElement.isLoading
    )
      return;
    const selectedMeaElementId = selectedMeaElement.data?.data[0]?.id;
    const selectedWorkflowInstanceId = params.get("wf");
    if (!selectedMeaElementId && !selectedWorkflowInstanceId) {
      // no element or workflow selected, new element is created -> initial selections must be set for that
      const parentElementId = originalLocation[4];
      if (!parentElementId) return; // no parent selected, do not make initial selections
      let parentObjective;
      let parentPerspective;
      let parentTopElement;
      let tmp: any = undefined;
      tmp = data?.data?.find((element: any) => element.id === parentElementId);
      if (!tmp) return;
      if (
        hierarchyMode !== "Scorecards" &&
        preferredScorecard &&
        tmp.scorecardsymbol !== preferredScorecard
      ) {
        // use preferredscorecard if same parentelement exists there
        const parentfromPreferredSC = data.data?.find((element: any) => {
          return (
            element.symbol === tmp.symbol &&
            element.typename === tmp.typename &&
            element.scorecardsymbol === preferredScorecard
          );
        });
        if (parentfromPreferredSC) tmp = parentfromPreferredSC;
      }

      if (tmp.typename === "Perspective") parentPerspective = tmp;
      if (tmp.typename === "Strategic Objective") {
        parentObjective = tmp;
        parentPerspective = data.data?.find(
          (element: any) => element.id === tmp.parentelementid
        );
      }
      if (tmp.id === tmp.topelementid) {
        // top element was selected
        parentTopElement = tmp;
      }

      const newSelections = {
        ...INITIAL_VALUES,
        ...{
          parentelementid:
            parentObjective?.id ||
            parentPerspective?.id ||
            parentTopElement?.id ||
            "",
          location: {
            prioritysymbol: parentObjective?.symbol || "",
            themesymbol: parentPerspective?.symbol || "",
            topelementid: tmp.topelementid,
          },
        },
      };

      setINITIAL_VALUES(newSelections);
      setSelectedGE(tmp.topelementid);
      return;
    }

    const initialize = async () => {
      const initializeEdit = async (): Promise<ProjectValues> => {
        setEditLoading(true);
        let wfData: any;
        if (editType !== "existing") {
          const data = await axios.get(
            `workflow/instances/${selectedWorkflowInstanceId}`
          );
          wfData = data.data;
        } else {
          wfData = await axios
            .get(
              `workflow/modifyparams/${selectedMeaElementId}?includeSubElements=true`
            )
            .catch((err) => {
              if (err.response.status === 401) {
                alert(t("Unauthorized request!"));
                history.goBack();
              }
              return Promise.reject(err);
            });
        }

        setAllowedActions(
          (prevActions) => wfData?.data?.allowedActions || prevActions
        );
        let typeIsDelete: boolean = false;
        if (wfData?.data?.type === "delete") {
          typeIsDelete = true;
          wfData = await axios
            .get(`workflow/modifyparams/${wfData?.data?.meaId}`)
            .catch((err) => {
              if (err.response.status === 401) {
                alert(t("Unauthorized request!"));
                history.goBack();
              }
              return Promise.reject(err);
            });
        }

        if (editType === "existing") {
          setMeaId(selectedMeaElementId || "");
        } else {
          setEditWfId(selectedWorkflowInstanceId || "");
          setEditWfType(typeIsDelete ? "delete" : wfData?.data?.type || "");
        }

        if (wfData?.data?.parameters?.elements.length) {
          const projectValues = mapWorkflowToProjectValues(
            {
              parentHierarchy: data?.data,
              elementType: "Initiative",
              type: wfData?.data?.type,
              meaId: wfData?.data?.meaId,
              parameters: {
                elements: wfData?.data?.parameters?.elements,
              },
            },
            periodlevels || []
          );
          setPeriods(
            projectValues.periodlevelid,
            projectValues.scheduleAndBudget["Project Start Date"],
            projectValues.scheduleAndBudget["Project End Date"]
          );
          setSelectedGE(projectValues.location?.topelementid);
          return projectValues;
        }
        return EMPTY_INITIAL_VALUES;
      };

      const editValues = await initializeEdit();

      if (editValues.meaId) {
        setMeaId(editValues.meaId);
      }

      setINITIAL_VALUES(editValues);
      setEditLoading(false);
    };

    initialize();
  }, [data, isLoading, selectedMeaElement.isLoading, selectedMeaElement.data]);

  useEffect(() => {
    const symbol = undefined; // INITIAL_VALUES?.linkToDubaiPlan?.themeSymbol;
    if (symbol) {
      filterByTheme({ symbol });
    }
  }, [INITIAL_VALUES, themeOptionsBySymbol]);

  const formRef = useRef<FormikProps<any>>(null);

  const setThemes = () => {
    const opts: any[] = [
      ...new Set(
        data?.data?.filter((d: any) => {
          if (d.typename !== "Perspective") return false;
          return !selectedGE || d.topelementid === selectedGE;
        })
      ),
    ];
    setThemeOptions(opts);
  };

  const setPriorities = () => {
    const opts: any[] = [
      ...new Set(
        data?.data?.filter((d: any) => {
          if (d.typename !== "Strategic Objective") return false;
          return d.topelementid === selectedGE;
        })
      ),
    ];
    setPrioritiesOptions(opts);
  };

  useEffect(() => {
    setThemes();
    setPriorities();

    const GEs: any = [
      ...new Set(
        data?.data?.filter((d: any) => d.typename === "Organization") || []
      ),
    ];
    setINITIAL_VALUES((prev) => {
      return prev;
    });

    setGEOptions([...GEs]);
  }, [data, selectedGE]);

  if (
    isLoading ||
    roleLoading ||
    editLoading ||
    unitsLoading ||
    valueSettingsLoading ||
    periodlevelsLoading ||
    selectedMeaElement.isLoading
  ) {
    return <Loader />;
  }

  const disableForm =
    status === "successSubmit" ||
    status === "successAdmin" ||
    status === "successApprove" ||
    status === "successReject" ||
    (!allowedActions.includes("allowedit") &&
      !allowedActions.includes("editAll")) ||
    loading;
  const disableButtons =
    loading ||
    status === "rejecting" ||
    status === "successReject" ||
    status === "successAdmin" ||
    status === "successApprove" ||
    status === "successSubmit" ||
    status === "approving";
  const showSubmit =
    allowedActions.includes("submit-disabled") ||
    allowedActions.includes("submit-enabled");

  const convertFormikValuesToKPIValues = (
    formikmeasurevalues: any[],
    periodlevelid: string,
    periodlevels: IPeriodLevel[] | undefined
  ): Value[] => {
    const periods = periodlevels?.find(
      (level: any) => level.id === periodlevelid
    )?.periods;
    if (!periods) return [];
    const kpiValues: Value[] = [];
    for (let i = 0; i < formikmeasurevalues.length; i += 1) {
      if (formikmeasurevalues[i]) {
        for (const [key, kpivalue] of Object.entries(formikmeasurevalues[i])) {
          kpiValues.push({
            series: key,
            period: periods[i].name,
            value: kpivalue ? (kpivalue as string) : "",
          });
        }
      }
    }
    return kpiValues;
  };

  const getTitle = () => {
    if (editWfId && editWfType) {
      if (editWfType === "modify") return t("initiatives.Edit Project");
      if (editWfType === "create") return t("initiatives.Create Project");
      if (editWfType === "delete") return t("initiatives.Delete Project");
      return t("Undefined operation");
    }
    return editType === "existing"
      ? t("initiatives.Edit Project")
      : t("initiatives.Create Project");
  };

  const visibleUnits = conf.getProjectEditingViewVisibleUnits();

  return (
    <>
      <GenericToolbar
        title={getTitle()}
        icon={"/icons/navitems/Projects.svg"}
        hidePeriodSelector={true}
      />
      <Formik
        initialValues={INITIAL_VALUES}
        validateOnBlur
        innerRef={formRef}
        enableReinitialize
        validateOnChange={false}
        validationSchema={ValidationSchema()}
        onSubmit={(formikData) => {
          const submit = async () => {
            // const noAccess = setAccessErr();

            const noAccess = false;
            if (noAccess) return false;
            setLoading(true);
            const res = await SubmitProjectForm(
              formikData,
              editWfId,
              meaId,
              periodlevels || [],
              valueSettings || [],
              isAdmin
            );
            setLoading(false);
            if (res.status === "success") {
              setStatus(res.wfid ? "successSubmit" : "successAdmin");
            } else {
              setStatus(res.status);
            }
          };
          submit();
        }}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          errors,
          setFieldValue,
          submitCount,
          validateForm,
          setTouched,
          touched,
        }) => (
          <div className={classes.screenContainer}>
            <div className={classes.mainContent}>
              <Form>
                <Anchor name="basicInfo" />
                <BasicInfoForm
                  isProject={true}
                  disabled={disableForm}
                  setField={setFieldValue}
                />
                <Anchor name="elementLocation" />
                <ElementOwnershipForm
                  GovernmentEntities={GEOptions}
                  selectedGE={selectedGE}
                  setSelectedGE={setSelectedGE}
                  setFieldValue={setFieldValue}
                  disabled={disableForm}
                  GEOwnerSelectionDisabled={false}
                  isProject={true}
                />
                <ElementLocationForm
                  setFieldValue={setFieldValue}
                  themeOptions={selectedGE ? themeOptionsBySymbol : {}}
                  priorityOptions={selectedGE ? priorityOptionsBySymbol : {}}
                  filterByTheme={filterByTheme}
                  selectedGE={selectedGE}
                  disabled={disableForm}
                />
                <Anchor name="measurementDetails" />
                <MeasurementDetailsForm
                  disabled={disableForm}
                  setField={setFieldValue}
                  unitId={values.measureunitid}
                  valuesettingId={values.valuesettingid}
                  currentStartDate={
                    values.scheduleAndBudget["Project Start Date"]
                  }
                  currentEndDate={values.scheduleAndBudget["Project End Date"]}
                  setVisiblePeriods={setPeriods}
                  valuesettings={valueSettings}
                  units={(units || []).filter(
                    (unit: any) =>
                      !visibleUnits || visibleUnits.includes(unit.name)
                  )}
                  isProject={true}
                  editing={!!(params.get("id") || params.get("wf"))}
                />
                <Anchor name="scheduleAndBudget" />
                <ScheduleAndBudgetForm
                  disabled={disableForm}
                  currentStartDate={
                    values.scheduleAndBudget["Project Start Date"]
                  }
                  currentEndDate={values.scheduleAndBudget["Project End Date"]}
                  unitId={values.measureunitid}
                  setVisiblePeriods={setPeriods}
                  periodlevelId={values.periodlevelid}
                  units={units}
                />
                <Anchor name="values" />
                <ValuesForm
                  isProject
                  periodlevelId={values.periodlevelid}
                  valueSettingId={values.valuesettingid}
                  visiblePeriods={visiblePeriods}
                  units={units}
                  unitId={values.measureunitid}
                  valuesettings={valueSettings?.data || []}
                  disabled={
                    disableForm ||
                    values.scheduleAndBudget["Project End Date"] ===
                      undefined ||
                    values.scheduleAndBudget["Project Start Date"] === undefined
                  }
                  projectDates={[
                    values.scheduleAndBudget["Project Start Date"] || "",
                    values.scheduleAndBudget["Project End Date"] || "",
                  ]}
                  projectValues={values.projectValues}
                />
                <div style={{ height: "25px" }}></div>
                <Anchor name="milestones" />
                <SubInfo
                  currentList={values.Milestones}
                  mainFormChange={setFieldValue}
                  periodlevelId={values.periodlevelid}
                  isMilestone={true}
                  periods={visiblePeriods}
                  formName="Milestones"
                  disabled={
                    disableForm ||
                    values.scheduleAndBudget["Project Start Date"] ===
                      undefined ||
                    values.scheduleAndBudget["Project End Date"] === undefined
                  }
                  projectDates={[
                    values.scheduleAndBudget["Project Start Date"] || "",
                    values.scheduleAndBudget["Project End Date"] || "",
                  ]}
                  originalLocation={originalLocation}
                  disableButtons={disableForm || disableButtons}
                  unit={{
                    "en-gb": "%",
                    ar: "%",
                  }}
                  ref={milestonesRef}
                />
              </Form>
              {/*
                <div style={{ display: "flex" }}>
                  <pre>{JSON.stringify(values, null, 2)}</pre>
                </div>
                
                 <pre>{JSON.stringify(getMetaData(values), null, 2)}</pre> */}
              {/* <div>
              <pre>{JSON.stringify(errors, null, 2)}</pre>
            </div> */}
            </div>
            <div className={classes.stepper}>
              <div>
                <ComponentThemes.PageContentMenu>
                  <PageContentMenu
                    componentData={{
                      items: [
                        { title: t("Basic Information"), anchor: "basicInfo" },
                        {
                          title: t("initiatives.Project Location"),
                          anchor: "elementLocation",
                        },
                        {
                          title: t("initiatives.Measurement Details"),
                          anchor: "measurementDetails",
                        },
                        {
                          title: t("initiatives.Schedule and Budget"),
                          anchor: "scheduleAndBudget",
                        },
                        { title: t("initiatives.Values"), anchor: "values" },
                        {
                          title: t("initiatives.Milestones"),
                          anchor: "milestones",
                        },
                      ],
                    }}
                  />
                </ComponentThemes.PageContentMenu>
                <div className={buttonClasses.btncontainer}>
                  <StatusHandler
                    requiredFields={
                      Object.keys(errors).length !== 0 &&
                      (submitCount !== 0 || approveCount !== 0)
                    }
                    invalidAccess={
                      (submitCount !== 0 || approveCount !== 0) && invalidAccess
                    }
                    showSubmit={showSubmit}
                    status={status}
                    type={editType}
                    isProject={true}
                  />

                  {showSubmit ? (
                    <Button
                      className={clsx(
                        buttonClasses.button,
                        buttonClasses.submitButton
                      )}
                      type="submit"
                      onClick={() => handleSubmit()}
                      disabled={
                        disableButtons ||
                        allowedActions.includes("submit-disabled")
                      }
                    >
                      {!loading ? t("Submit") : t("Submitting...")}
                    </Button>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                      }}
                    >
                      <div>
                        <Button
                          className={clsx(
                            buttonClasses.button,
                            buttonClasses.submitButton
                          )}
                          onClick={() => {
                            const approve = async () => {
                              setLoading(true);
                              setStatus("approving");

                              const res = await ApproveProjectForm(
                                editWfId,
                                values,
                                periodlevels || [],
                                valueSettings || [],
                                INITIAL_VALUES,
                                allowedActions.includes("allowedit"),
                                meaId
                              );
                              console.log("APPROVE RESULT", res);
                              setLoading(false);
                              if (res.status === 200) {
                                setStatus("successApprove");
                              } else {
                                setStatus("fail");
                              }
                            };
                            validateForm().then((errs: any) =>
                              setTouched({ ...errs, ...touched })
                            );
                            setApproveCount((prev) => prev + 1);
                            if (
                              JSON.stringify(errors) === JSON.stringify({}) &&
                              validateSubForms()
                            ) {
                              approve();
                            }
                          }}
                          disabled={
                            disableButtons ||
                            allowedActions.includes("approve-disabled")
                          }
                        >
                          {!(status === "approving")
                            ? t(
                                `Approve${
                                  editType === "delete" ? " Deletion" : ""
                                }`
                              )
                            : t("Approving...")}
                        </Button>
                      </div>
                      <div>
                        <Button
                          style={{
                            background: "red",
                          }}
                          className={buttonClasses.button}
                          disabled={
                            disableButtons ||
                            allowedActions.includes("reject-disabled")
                          }
                          onClick={() => {
                            const reject = async () => {
                              setStatus("rejecting");
                              const res = await RejectProjectForm(editWfId);
                              setLoading(false);
                              console.log("REJECT RESULT", res);
                              if (res.status === 200) {
                                setStatus("successReject");
                              } else {
                                setStatus("fail");
                              }
                            };
                            reject();
                          }}
                        >
                          {!(status === "rejecting")
                            ? t(
                                `Reject${
                                  editType === "delete" ? " Deletion" : ""
                                }`
                              )
                            : t("Rejecting...")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Formik>
    </>
  );
};

export default ProjectsForm;
